import React, { useEffect, useMemo, useState } from 'react';
import { Box, getDuration, Grid, useDebounce } from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import { useCurrentOrganization } from '@/modules/core';
import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import AccountTypeDropdown from '../AccountTypeDropdown';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent, useMultipleEmployerSelectAvailable } from '../hooks';
import MultipleEmployerSelector from '../MultipleEmployerSelector';
import PlaceholderBase from '../PlaceholderBase';
import PlanSelector from '../PlanSelector';
import PlanYearSelector from '../PlanYearSelector';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import { ReportSummaryBase, ReportSummaryWrapper } from '../ReportSummary';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import { DelinquentClaimsSummaryReportParams } from './DelinquentClaimsReport.types';
import {
  useDelinquentClaimsReportExportSummaryCSVQuery,
  useDelinquentClaimsReportSummaryQuery,
} from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: DelinquentClaimsSummaryReportParams;
  onSetFilterValue: FilterSetters<DelinquentClaimsSummaryReportParams>;
}

const DelinquentClaimsReportSummary: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const {
    observingOrganization: { type },
  } = useCurrentOrganization();
  const isPartnerOrDistributor = useMemo(() => {
    return type === OrganizationTypes.partner || type === OrganizationTypes.distributor;
  }, [type]);
  const isSystemLevel = useMemo(() => type === OrganizationTypes.system, [type]);
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues, {
    isMultipleEmployerMode: true,
  });
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();
  const [isAutoFocus, setAutoFocus] = useState(false);

  const debouncedAccountTypes = useDebounce(filtersValues.accountTypes, getDuration({ seconds: 0.5 }));
  const debouncedPlanYearPeriod = useDebounce(filtersValues.planYearPeriod, getDuration({ seconds: 0.5 }));
  const debouncedPlanIds = useDebounce(filtersValues.planIds, getDuration({ seconds: 0.5 }));

  const {
    isLoading,
    data,
    planOptions,
    isEmptyPlanOptions,
    lastTimeUpdateDate,
    isEmptyData,
    isLoadingMore,
    isHasNextPage,
    handleLoadMore,
  } = useDelinquentClaimsReportSummaryQuery({
    employerId: filtersValues.employerId,
    isMultipleEmployer: isMultipleEmployerAvailable,
    employerIds: filtersValues.employerIds,
    accountTypes: debouncedAccountTypes,
    planIds: debouncedPlanIds,
    planYearPeriod: debouncedPlanYearPeriod,
  });

  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useDelinquentClaimsReportExportSummaryCSVQuery({
      employerId: filtersValues.employerId,
      isMultipleEmployer: isMultipleEmployerAvailable,
      employerIds: filtersValues.employerIds,
      accountTypes: filtersValues.accountTypes,
      planIds: filtersValues.planIds,
      planYearPeriod: filtersValues.planYearPeriod,
    });

  useEffect(() => {
    onSetFilterValue.planIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersValues.employerId]);

  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }
    if (!isLoading && !data.length) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }

    return (
      <ReportSummaryBase
        isPreviewMode={isPreviewMode}
        isLoading={isLoading}
        data={data}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        isLoadingMore={isLoadingMore}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtersValues.employerId, filtersValues.employerIds, isLoading, isPreviewMode]);

  return (
    <TopButtonPortalWrapper
      isPreviewMode={isPreviewMode}
      infoNode={
        <ReportLastUpdateDate
          isLoading={isLoading}
          key={`${filtersValues.employerId}`}
          value={lastTimeUpdateDate}
        />
      }
      buttonNode={
        <Box direction="row">
          <AddScheduledReportButton
            employerId={filtersValues.employerId}
            employerIds={filtersValues.employerIds}
            isMultipleEmployer={isMultipleEmployerAvailable}
            type={ReportTypeKeys.DelinquentClaims}
            tab={ReportTab.Summary}
          />
          <Box margin={{ left: 'spacing12' }}>
            <ExportButton
              onClick={handleExportCSV}
              isLoading={isExportLoading}
              disabled={!isEmployerIsPresent || isEmptyData}
              text="Download Summary (CSV)"
            />
          </Box>
        </Box>
      }
    >
      <ReportSummaryWrapper
        isPreviewMode={isPreviewMode}
        filters={
          <Box fill direction="row">
            <Grid
              fill
              columns={
                isPreviewMode
                  ? {
                      count: 3,
                      size: 'auto',
                    }
                  : {
                      count: !isPartnerOrDistributor && !isSystemLevel ? 4 : 3,
                      size: 'auto',
                    }
              }
              gap="spacing12"
            >
              {isMultipleEmployerAvailable ? (
                <MultipleEmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  values={filtersValues.employerIds}
                  onChangeValues={onSetFilterValue.employerIds}
                />
              ) : (
                <EmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  value={filtersValues.employerId}
                  onChangeValue={onSetFilterValue.employerId}
                />
              )}

              <PlanYearSelector
                disabled={
                  !isEmployerIsPresent ||
                  (!!isMultipleEmployerAvailable && filtersValues.employerIds?.length !== 1)
                }
                value={filtersValues.planYearPeriod}
                onChangeValue={onSetFilterValue.planYearPeriod}
              />

              {!isPartnerOrDistributor && (
                <Box key={`plan-selector ${isLoading}`}>
                  <PlanSelector
                    disabled={!isEmployerIsPresent || isEmptyPlanOptions}
                    options={planOptions}
                    values={filtersValues.planIds}
                    onChangeValues={onSetFilterValue.planIds}
                  />
                </Box>
              )}
              {!isSystemLevel && (
                <AccountTypeDropdown
                  disabled={!isEmployerIsPresent}
                  values={filtersValues.accountTypes}
                  onChangeValues={onSetFilterValue.accountTypes}
                />
              )}
            </Grid>
          </Box>
        }
      >
        {childrenNode}
      </ReportSummaryWrapper>
    </TopButtonPortalWrapper>
  );
};

export default DelinquentClaimsReportSummary;
